import { pageUrl } from '@constants/url';
import {
  Ban,
  BarChart3,
  Bell,
  Boxes,
  ChevronsLeft,
  ChevronsRight,
  CirclePlus,
  ClipboardCheck,
  Cloud,
  CreditCard,
  Crown,
  Database,
  DollarSign,
  FileArchive,
  FileInput,
  Forward,
  Gamepad2,
  GraduationCap,
  History,
  Landmark,
  List,
  Mail,
  Moon,
  Mountain,
  Newspaper,
  Power,
  Radar,
  Reply,
  Scissors,
  Search,
  Settings,
  Shield,
  Ship,
  Stamp,
  Sun,
  Table2,
  Truck,
  UsersRound,
  Warehouse,
  X,
  Building2,
  Factory,
} from 'lucide-react';
import LinksGroup from '@components/sidebar/links-group';
import { Tooltip, Menu } from '@mantine/core';
import useAuth from '@hooks/use-auth';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useState, useEffect, useMemo, useRef } from 'react';
import { impersonationUtils } from '@utils/impersonation';
import { isSuperAdminEmail } from '@utils/access';
import ImpersonationSelect from '@components/impersonation/ImpersonationSelect';
import LanguageSwitcher from '@components/language/LanguageSwitcher';
import { isProfessionalModeEnabled } from '@utils/professionalMode';
import { useCompanyFeatures } from '@contexts/CompanyFeaturesContext';
import { usePermissions } from '@contexts/PermissionsContext';
import { BILLING_ENABLED } from '@constants/features';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import FeatureLockedModal from '@components/feature-lock/FeatureLockedModal';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useHaptic, haptic, HAPTIC } from '@utils/haptic';
import { LIGHT_THEME_MODES, SELECTABLE_THEME_MODES, type ThemeMode } from '@constants/colors';
import { sidebarAnchor, TourHelpButton } from '@/features/onboarding';
import { useUnreadPatchLogCount } from '@hooks/patch-logs/use-patch-logs';

/** Themes whose sidebar surface is light — active pills must use dark text. */
const LIGHT_SIDEBAR_MODES: ThemeMode[] = LIGHT_THEME_MODES;

/**
 * Apply `alpha` to a palette colour.
 *
 * Needed because the two palettes in here are different shapes: `accent` is a
 * hex string but `aurora` holds `rgba(...)` strings. Appending hex-alpha to an
 * rgba() string produces `rgba(37, 99, 235, 0.16)30`, and one invalid stop
 * voids the whole gradient function, so the header painted nothing at all.
 */
const withAlpha = (color: string, alpha: number): string => {
  const hex = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (hex) {
    const n = Number.parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(color.trim());
  if (rgb) {
    const [r, g, b] = rgb[1].split(',').map((v) => v.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};
/** Align with Mantine AppShell `md` (~992px). */
const MOBILE_BREAKPOINT = 992;

const iconClass = 'shrink-0';
const navIcon = (Icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>) => (
  <Icon className={iconClass} size={16} strokeWidth={1.5} />
);

interface SidebarV2Props {
  isOpen: boolean;
  isMinimized: boolean;
  toggleDrawer: () => void;
  toggleMinimize: () => void;
  isVisible?: boolean;
}

// Emails that are view-only for Kepabeanan (no edit in both BC 2.0 and BC 3.0)
const KEPABEANAN_VIEW_ONLY_EMAILS = [
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'panji@gateway-id.com',
];

// Emails that are view-only for Manifest
const MANIFEST_VIEW_ONLY_EMAILS = [
  'panji@gateway-id.com',
  'anne.veronica@molgroup.com',
  'ikumi.shibata1@molgroup.com',
  'christina.vonny@molgroup.com',
  'ari.wibowo@molgroup.com',
  'femy.muthia@molgroup.com',
  'reza.haqqa@molgroup.com',
  'ratri.puji@molgroup.com',
  'mlg-id.airexport01@molgroup.com',
];

const menus = [
  {
    label: 'Mgmt Dashboard',
    i18nKey: 'menu.mgmtDashboard',
    icon: navIcon(Crown),
    link: pageUrl.managementDashboard,
    badge: 'BOD',
  },
  {
    label: 'Manifest',
    i18nKey: 'menu.manifest',
    icon: navIcon(Ship),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: '/manifest/dashboard',
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/manifest/product',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: '/manifest/draft',
        isMinimized: false,
      },
      {
        label: 'Doc Manifes',
        i18nKey: 'menu.docManifes',
        icon: navIcon(FileInput),
        link: '/manifest/doc-pabean',
        isMinimized: false,
      },
      {
        label: 'The Guardian',
        i18nKey: 'menu.theGuardian',
        icon: navIcon(Bell),
        link: '/manifest/guardian',
        isMinimized: false,
      },
      {
        label: 'Radar',
        i18nKey: 'menu.radar',
        icon: navIcon(Radar),
        link: '/manifest/guardian/radar',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Kepabeanan',
    i18nKey: 'menu.kepabeanan',
    icon: navIcon(FileInput),
    link: '#',
    subLinks: [
      {
        label: 'Dashboard',
        i18nKey: 'menu.dashboard',
        icon: navIcon(BarChart3),
        link: pageUrl.dashboard,
        isMinimized: false,
      },
      {
        label: 'Buat Aju',
        i18nKey: 'menu.buatAju',
        icon: navIcon(CirclePlus),
        link: '/buat-aju',
        isMinimized: false,
      },
      {
        label: 'Doc Pabean',
        i18nKey: 'menu.docPabean',
        icon: navIcon(FileInput),
        link: pageUrl.docPabean,
        isMinimized: false,
      },
      {
        label: 'Monitoring Blokir',
        i18nKey: 'menu.monitoringBlokir',
        icon: navIcon(Ban),
        link: '/monitoring-blokir',
        isMinimized: false,
      },
      {
        label: 'Draft',
        i18nKey: 'menu.draft',
        icon: navIcon(List),
        link: pageUrl.draftDocPabean,
        isMinimized: false,
      },
      {
        label: 'Respon',
        i18nKey: 'menu.respon',
        icon: navIcon(Reply),
        link: '/respon',
        isMinimized: false,
      },
      {
        label: 'Database Barang',
        i18nKey: 'menu.databaseBarang',
        icon: navIcon(Database),
        link: '/database-barang',
        isMinimized: false,
      },
      {
        label: 'PPFTZ',
        i18nKey: 'menu.ppftz',
        icon: navIcon(Warehouse),
        link: pageUrl.ppftz.list,
        isMinimized: false,
        badge: 'New',
      },
    ],
  },
  {
    label: 'Kuota Impor / Ekspor',
    i18nKey: 'menu.kuotaImporEkspor',
    icon: navIcon(Table2),
    link: '#',
    subLinks: [
      {
        label: 'Shipment Quota',
        i18nKey: 'menu.shipmentQuota',
        icon: navIcon(Table2),
        link: '/shipment-quota',
        isMinimized: false,
      },
      {
        label: 'Harga Referensi',
        i18nKey: 'menu.hargaReferensi',
        icon: navIcon(DollarSign),
        link: '/harga-referensi',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Cukai',
    i18nKey: 'menu.cukai',
    icon: navIcon(Stamp),
    link: '#',
    badge: 'New',
    subLinks: [
      {
        label: 'Tutorial',
        i18nKey: 'menu.tutorial',
        icon: navIcon(GraduationCap),
        link: '/cukai/tutorial',
        isMinimized: false,
        badge: 'Mulai',
      },
      {
        label: 'Surat Jalan',
        i18nKey: 'menu.suratJalan',
        icon: navIcon(Truck),
        link: '/cukai/surat-jalan',
        isMinimized: false,
      },
      {
        label: 'Dokumen CK',
        i18nKey: 'menu.dokumenCk',
        icon: navIcon(Stamp),
        link: '/cukai/documents',
        isMinimized: false,
      },
      {
        label: 'Bill of Material',
        i18nKey: 'menu.billOfMaterial',
        icon: navIcon(Boxes),
        link: '/cukai/bom',
        isMinimized: false,
      },
      {
        label: 'Inventori',
        i18nKey: 'menu.inventori',
        icon: navIcon(Warehouse),
        link: '/cukai/inventory',
        isMinimized: false,
      },
      {
        label: 'Risk Self-Check',
        i18nKey: 'menu.riskSelfCheck',
        icon: navIcon(Shield),
        link: '/cukai/compliance',
        isMinimized: false,
        badge: 'AI Ready',
      },
    ],
  },
  {
    label: 'Customer Approval',
    i18nKey: 'menu.customerApproval',
    icon: navIcon(ClipboardCheck),
    link: '#',
    subLinks: [
      {
        label: 'Approval Requests',
        i18nKey: 'menu.approvalRequests',
        icon: navIcon(ClipboardCheck),
        link: '/customer-approval',
        isMinimized: false,
      },
      {
        label: 'Email Groups',
        i18nKey: 'menu.emailGroups',
        icon: navIcon(List),
        link: '/customer-approval/email-groups',
        isMinimized: false,
      },
      {
        label: 'Reviewers',
        i18nKey: 'menu.reviewers',
        icon: navIcon(UsersRound),
        link: '/customer-approval/reviewers',
        isMinimized: false,
      },
      {
        label: 'Roles & Permissions',
        i18nKey: 'menu.rolesPermissions',
        icon: navIcon(Shield),
        link: '/customer-approval/roles',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Credit Topup',
    i18nKey: 'menu.creditTopup',
    icon: navIcon(CreditCard),
    link: '/credit/topup',
  },
  {
    label: 'Langganan',
    i18nKey: 'menu.langganan',
    icon: navIcon(Crown),
    link: pageUrl.subscription,
  },
  {
    label: 'Berita',
    i18nKey: 'menu.berita',
    icon: navIcon(Newspaper),
    link: '#',
    subLinks: [
      {
        label: 'Antara News',
        i18nKey: 'menu.antaraNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=antara',
        isMinimized: false,
      },
      {
        label: 'CNN Indonesia',
        i18nKey: 'menu.cnnIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnn',
        isMinimized: false,
      },
      {
        label: 'CNBC Indonesia',
        i18nKey: 'menu.cnbcIndonesia',
        icon: navIcon(Newspaper),
        link: '/berita?source=cnbc',
        isMinimized: false,
      },
      {
        label: 'Republika News',
        i18nKey: 'menu.republikaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=republika',
        isMinimized: false,
      },
      {
        label: 'Kumparan News',
        i18nKey: 'menu.kumparanNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=kumparan',
        isMinimized: false,
      },
      {
        label: 'VOA News',
        i18nKey: 'menu.voaNews',
        icon: navIcon(Newspaper),
        link: '/berita?source=voa',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Layanan Lainnya',
    i18nKey: 'menu.layananLainnya',
    icon: navIcon(Settings),
    link: '#',
    subLinks: [
      {
        label: 'Stamp Duty',
        i18nKey: 'menu.stampDuty',
        icon: navIcon(FileInput),
        link: pageUrl.stampDuty,
        isMinimized: false,
      },
      {
        label: 'Mini Games',
        i18nKey: 'menu.miniGames',
        icon: navIcon(Gamepad2),
        link: '/mini-games',
        isMinimized: false,
        badge: 'New',
      },
      {
        label: 'File Compress',
        i18nKey: 'menu.fileCompress',
        icon: navIcon(FileArchive),
        link: pageUrl.fileCompression,
        isMinimized: false,
      },
      {
        label: 'Split PDF',
        i18nKey: 'menu.splitPdf',
        icon: navIcon(Scissors),
        link: '/split-pdf',
        isMinimized: false,
        badge: 'New',
      },
      {
        label: 'Email Groups',
        i18nKey: 'menu.emailGroups',
        icon: navIcon(ClipboardCheck),
        link: '/customer-approval/email-groups',
        isMinimized: false,
      },
    ],
  },
  {
    label: 'Master',
    i18nKey: 'menu.master',
    icon: navIcon(Database),
    link: '#',
    subLinks: [
      {
        label: 'Ceisa User',
        i18nKey: 'menu.ceisaUser',
        icon: navIcon(UsersRound),
        link: '/master/ceisa-user',
        isMinimized: false,
      },
      {
        label: 'User',
        i18nKey: 'menu.user',
        icon: navIcon(UsersRound),
        link: '/master/user',
        isMinimized: false,
      },
      {
        label: 'Sea Carriers',
        i18nKey: 'menu.seaCarriers',
        icon: navIcon(Ship),
        link: '/master/sea-carriers',
        isMinimized: false,
      },
      {
        label: 'Export Bank',
        i18nKey: 'menu.exportBank',
        icon: navIcon(Landmark),
        link: '/master/export-bank',
        isMinimized: false,
      },
      {
        label: 'Email',
        i18nKey: 'menu.email',
        icon: navIcon(Mail),
        link: '/master/email',
        isMinimized: false,
      },
      {
        label: 'Customer Email Forwarding',
        i18nKey: 'menu.customerEmailForwarding',
        icon: navIcon(Forward),
        link: pageUrl.customerEmailForwarding,
        isMinimized: false,
      },
      {
        label: 'Statement',
        i18nKey: 'menu.statement',
        icon: navIcon(ClipboardCheck),
        link: '/master/statement',
        isMinimized: false,
      },
      {
        label: 'Data Consignee',
        i18nKey: 'menu.dataConsignee',
        icon: navIcon(Building2),
        link: '/master/consignee',
        isMinimized: false,
      },
      {
        label: 'Data Eksportir',
        i18nKey: 'menu.dataEksportir',
        icon: navIcon(Factory),
        link: '/master/eksportir',
        isMinimized: false,
      },
      {
        label: 'Manifest Reminder',
        i18nKey: 'menu.manifestReminder',
        icon: navIcon(Bell),
        link: '/master/manifest-reminder',
        isMinimized: false,
      },
      {
        label: 'Revoke Access',
        i18nKey: 'menu.revokeAccess',
        icon: navIcon(Ban),
        link: '/master/revoke-access',
        isMinimized: false,
      },
    ],
  },
];

/**
 * Every navigable deep link the sidebar knows about, flattened.
 *
 * MenuAccessGuard checks a directly-typed URL against this catalog so route
 * access and sidebar visibility are decided from the same source: if a path
 * belongs to a menu entry the user's role did not grant, the page is blocked
 * rather than merely hidden from the nav.
 */
export const ALL_MENU_LINKS: string[] = menus
  .flatMap((item) => [
    item.link,
    ...((item as { subLinks?: { link?: string }[] }).subLinks ?? []).map((sub) => sub.link),
  ])
  .filter((link): link is string => typeof link === 'string' && link !== '' && link !== '#')
  .map((link) => link.split('?')[0]);

/* ------------------------------------------------------------------ *
 *  Shared pieces
 *
 *  The rail and the onboarding film (src/features/first-login) render the
 *  same identity, the same menu filtering, the same surface and the same
 *  header. They live here, next to `menus`, so the film cannot drift from
 *  what the user actually sees.
 * ------------------------------------------------------------------ */

/** Who is looking at the rail: email, impersonation and professional mode, kept in sync with storage. */
export const useSidebarIdentity = () => {
  const [userEmail, setUserEmail] = useState<string>('');
  const [canImpersonate, setCanImpersonate] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [professionalModeEnabled, setProfessionalModeEnabled] = useState<boolean>(false);

  const updateUserInfo = () => {
    const authData = localStorage.getItem('authData');
    const impersonating = impersonationUtils.isImpersonating();

    setIsImpersonating(impersonating);

    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const email = parsed.email || '';
        setUserEmail(email);

        // For impersonation permissions, check original user's email when impersonating
        if (impersonating) {
          const originalAuthData = localStorage.getItem('originalAuthData');
          if (originalAuthData) {
            const originalParsed = JSON.parse(originalAuthData);
            const originalEmail = originalParsed.email || '';
            setCanImpersonate(impersonationUtils.canImpersonate(originalEmail));
          } else {
            setCanImpersonate(false);
          }
        } else {
          // When not impersonating, use current user's email for permissions
          setCanImpersonate(impersonationUtils.canImpersonate(email));
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
        setUserEmail('');
        setCanImpersonate(false);
      }
    } else {
      setUserEmail('');
      setCanImpersonate(false);
    }
  };

  useEffect(() => {
    updateUserInfo();

    // Listen for storage changes to update UI when impersonation state changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authData' || e.key === 'impersonationSession' || e.key === 'originalAuthData') {
        updateUserInfo();
      }
    };

    // Listen for custom events from impersonation actions
    const handleImpersonationChange = () => {
      updateUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  useEffect(() => {
    const updateProfessionalMode = () => {
      setProfessionalModeEnabled(isProfessionalModeEnabled());
    };

    updateProfessionalMode();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', updateProfessionalMode);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', updateProfessionalMode);
      }
    };
  }, []);

  return { userEmail, canImpersonate, isImpersonating, professionalModeEnabled };
};

/** The menu list exactly as the rail shows it for this user, plus the module lock lookup. */
export const useSidebarMenus = ({
  userEmail,
  professionalModeEnabled,
  searchQuery = '',
}: {
  userEmail: string;
  professionalModeEnabled: boolean;
  searchQuery?: string;
}) => {
  const displayedMenus = useMemo(() => {
    const email = userEmail.toLowerCase();
    const isKepabeananViewOnly = KEPABEANAN_VIEW_ONLY_EMAILS.includes(email);
    const isManifestViewOnly = MANIFEST_VIEW_ONLY_EMAILS.includes(email);

    let result = menus;

    // Billing (Credit Topup + Langganan) is hidden by build flag, not by role:
    // the routes themselves redirect to /dashboard in App.tsx.
    if (!BILLING_ENABLED) {
      result = result.filter((item) => item.label !== 'Credit Topup' && item.label !== 'Langganan');
    }

    if (professionalModeEnabled) {
      result = result.map((item) => {
        if (item.label !== 'Layanan Lainnya' || !item.subLinks) {
          return item;
        }
        return {
          ...item,
          subLinks: item.subLinks.filter((subItem) => subItem.label !== 'Stamp Duty' && subItem.label !== 'Mini Games'),
        };
      });
    }

    // For non-super-admin users, filter Master subLinks to only show User and Email
    if (!isSuperAdminEmail(email)) {
      result = result.map((item) => {
        if (item.label !== 'Master' || !item.subLinks) return item;
        return {
          ...item,
          subLinks: item.subLinks.filter(
            (sub) =>
              sub.label === 'User' ||
              sub.label === 'Email' ||
              sub.label === 'Customer Email Forwarding' ||
              sub.label === 'Statement' ||
              sub.label === 'Data Consignee' ||
              sub.label === 'Data Eksportir' ||
              sub.label === 'Manifest Reminder' ||
              sub.label === 'Export Bank'
          ),
        };
      });
    }

    // Hide "Buat Aju" for view-only users
    if (isKepabeananViewOnly || isManifestViewOnly) {
      result = result.map((item) => {
        if (!item.subLinks) return item;

        if (item.label === 'Kepabeanan' && isKepabeananViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        if (item.label === 'Manifest' && isManifestViewOnly) {
          return {
            ...item,
            subLinks: item.subLinks.filter((sub) => sub.label !== 'Buat Aju'),
          };
        }
        return item;
      });
    }

    return result;
  }, [professionalModeEnabled, userEmail]);

  const roleFilteredMenus = useMemo(() => {
    return displayedMenus.filter((item) => {
      if (userEmail.toLowerCase().includes('samuel')) {
        return item.label === 'Dashboard';
      }
      if (userEmail.toLowerCase().includes('fedex.com') && item.label === 'Mgmt Dashboard') {
        return false;
      }
      if (userEmail.toLowerCase().includes('demo')) {
        return item.label !== 'SSM' && item.label !== 'Stamp Duty';
      }
      if (userEmail.toLowerCase().includes('doc%')) {
        return item.label !== 'Kepabeanan';
      }
      if (item.label === 'Master') {
        if (isSuperAdminEmail(userEmail)) {
          return true;
        }
        if (
          userEmail.toLowerCase().includes('nissinti') &&
          userEmail.toLowerCase() !== 'ibnu.wardhana@nissinti.co.id'
        ) {
          return false;
        }
        return true;
      }
      return true;
    });
  }, [displayedMenus, userEmail]);

  // Custom-role menu access. `canAccessMenu` fails open only for users still
  // on their legacy role, so the sidebar narrows as soon as an admin assigns a
  // custom role — including one that grants no menus at all.
  const { canAccessMenu } = usePermissions();

  const permissionFilteredMenus = useMemo(() => {
    return roleFilteredMenus
      .map((item) => {
        if (!item.subLinks) return item;
        const subLinks = item.subLinks.filter((sub) => canAccessMenu(sub.link));
        return { ...item, subLinks };
      })
      .filter((item) => {
        // A group survives if any of its children survived; a plain link is
        // checked directly.
        if (item.subLinks) return item.subLinks.length > 0;
        return canAccessMenu(item.link);
      });
  }, [roleFilteredMenus, canAccessMenu]);

  // Per-company module locks: locked groups stay visible but dimmed, and
  // clicking them opens the contact-admin dialog instead of navigating.
  const { isFeatureEnabled } = useCompanyFeatures();

  const filteredMenus = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return permissionFilteredMenus;

    return permissionFilteredMenus
      .map((item) => {
        const labelMatch = item.label.toLowerCase().includes(q);
        if (!item.subLinks) {
          return labelMatch ? item : null;
        }
        const matchedSubs = item.subLinks.filter((sub) => sub.label.toLowerCase().includes(q));
        if (labelMatch) return item;
        if (matchedSubs.length > 0) {
          return { ...item, subLinks: matchedSubs };
        }
        return null;
      })
      .filter(Boolean) as typeof permissionFilteredMenus;
  }, [permissionFilteredMenus, searchQuery]);

  return { filteredMenus, isFeatureEnabled };
};

/** Colours and the glass/solid surface the rail paints, derived from the active theme. */
export const useSidebarSurface = () => {
  const { mode, colors: themeColors } = useTheme();
  const isDarkSidebar = !LIGHT_SIDEBAR_MODES.includes(mode);
  const hoverBg = isDarkSidebar ? 'rgba(255, 255, 255, 0.08)' : '#F5F5F5';
  const mutedText = isDarkSidebar ? 'rgba(255, 255, 255, 0.55)' : themeColors.textSecondary;
  const isApexBrand = mode === 'apex' || mode === 'apex-night';

  // APEX modes: the login page's glass material over the aurora — translucent
  // brand fill + backdrop blur (jv-glass-surface) + hairline rim + top-lit edge.
  // Other modes keep their solid/gradient sidebar.
  const sidebarStyle = isApexBrand
    ? {
        backgroundColor: isDarkSidebar ? 'rgba(11, 28, 43, 0.78)' : 'rgba(255, 255, 255, 0.55)',
        backgroundImage: 'none',
        borderRight: `1px solid ${isDarkSidebar ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 42, 68, 0.14)'}`,
        color: themeColors.text,
        boxShadow: isDarkSidebar
          ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.16)'
          : 'inset 0 0 0 1px rgba(15, 42, 68, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
      }
    : {
        backgroundColor: themeColors.sidebar,
        backgroundImage: themeColors.sidebarGradient ?? themeColors.backgroundGradient,
        borderRight: `1px solid ${themeColors.border}`,
        color: themeColors.text,
      };

  // Futurist accent + aurora palette (follows active theme)
  const accent = themeColors.accent ?? themeColors.primary ?? '#2A6B62';
  // APEX modes: brand-blue aurora instead of the horizon teal
  const aurora: [string, string] = isApexBrand
    ? isDarkSidebar
      ? ['rgba(37, 99, 235, 0.32)', 'rgba(37, 99, 235, 0.16)']
      : ['rgba(37, 99, 235, 0.16)', 'rgba(37, 99, 235, 0.08)']
    : isDarkSidebar
      ? ['rgba(91, 125, 212, 0.7)', 'rgba(42, 107, 98, 0.55)']
      : ['rgba(42, 107, 98, 0.5)', 'rgba(91, 125, 212, 0.42)'];

  return { isDarkSidebar, hoverBg, mutedText, isApexBrand, sidebarStyle, accent, aurora };
};

/** Logo strip at the top of the rail. `onClose` renders the mobile drawer's close button. */
export const SidebarBrandHeader = ({
  isMinimized,
  isGatewayUser,
  onClose,
}: {
  isMinimized: boolean;
  isGatewayUser: boolean;
  onClose?: () => void;
}) => {
  const { colors: themeColors } = useTheme();
  const { isDarkSidebar, mutedText, accent, aurora } = useSidebarSurface();
  return (
    <div
      className={`flex-shrink-0 flex items-center relative overflow-hidden ${
        isMinimized ? 'justify-center px-2 h-12' : 'gap-2.5 px-3 h-12'
      }`}
      style={{ borderBottom: `1px solid ${themeColors.border}` }}
    >
      {/*
            The brand gradient now grades across the whole header rectangle
            instead of being trapped in a 30px ring, so the logo is free to
            fill the space. Static wash for the colour, moving sheen on top.
          */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(100deg, ${withAlpha(accent, 0.34)} 0%, transparent 44%, ${withAlpha(
            aurora[0],
            0.3
          )} 74%, transparent 100%)`,
        }}
        aria-hidden="true"
      />
      <div
        className="jv-header-sheen absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(100deg, transparent 35%, ${withAlpha(accent, 0.38)} 50%, transparent 65%)`,
        }}
        aria-hidden="true"
      />
      {/* Energy line sweep across the glass header */}
      <div className="jv-header-energyline" style={{ color: accent }} aria-hidden="true" />
      {isGatewayUser && (
        <img
          className={`shrink-0 ${isGatewayUser ? 'h-7 w-auto max-w-[100px] object-contain' : 'h-7 w-7'} rounded-md`}
          src="/image/jpg/gatewaycontainerline_logo.jpeg"
          alt="Gateway"
        />
      )}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md max-[991px]:flex hidden"
          style={{ color: mutedText }}
          aria-label="Close navigation"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
};

export interface SidebarFooterProps {
  isMinimized: boolean;
  userEmail: string;
  canImpersonate: boolean;
  isImpersonating: boolean;
  onLogout?: () => void;
  isLoggingOut?: boolean;
  onToggleMinimize?: () => void;
  /** Runs before a footer link navigates (the rail closes its mobile drawer). */
  onNavigate?: () => void;
  /** Paints a row in its hover state; the onboarding film uses it to point at Pengaturan. */
  highlight?: 'pengaturan';
  /**
   * Emit the `data-tour` anchors (help pill, language). The onboarding film
   * renders a second copy of this footer and passes false, so a running
   * tour's querySelector can only ever match the live rail.
   */
  tourAnchors?: boolean;
}

/**
 * The rail's footer: account row, impersonation, theme, Pengaturan, changelog,
 * help, language, logout, collapse. Same JSX the rail always had, now shared
 * with the onboarding film so the film shows the real footer.
 */
export const SidebarFooter = ({
  isMinimized,
  userEmail,
  canImpersonate,
  isImpersonating,
  onLogout,
  isLoggingOut = false,
  onToggleMinimize,
  onNavigate,
  highlight,
  tourAnchors = true,
}: SidebarFooterProps) => {
  const navigate = useNavigate();
  const { mode, toggleTheme, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { hoverBg, mutedText } = useSidebarSurface();
  // Shares the gate's query, so the badge and the blocking modal can never
  // disagree about what is unread.
  const unreadPatchLogs = useUnreadPatchLogCount();
  const hapticMedium = useHaptic('medium');
  const hapticFirm = useHaptic('firm');
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  const themeLabel =
    mode === 'apex'
      ? t('theme.apex')
      : mode === 'apex-night'
        ? t('theme.apexNight')
        : mode === 'horizon-night'
          ? t('theme.horizonNight')
          : mode === 'horizon'
            ? t('theme.horizon')
            : mode === 'dawn'
              ? t('theme.horizonDawn')
              : mode === 'dusk'
                ? t('theme.horizonDusk')
                : mode === 'mist'
                  ? t('theme.horizonMist')
                  : t('sidebar.theme');
  const ThemeIcon =
    mode === 'horizon-night' || mode === 'dusk' || mode === 'apex-night' ? (
      <Moon size={16} strokeWidth={1.5} />
    ) : (
      <Sun size={16} strokeWidth={1.5} />
    );

  const footerBtnClass = isMinimized
    ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150 relative'
    : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150 relative';

  const applyHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
  };
  const clearHover = (e: React.MouseEvent<HTMLElement>, bg = 'transparent') => {
    e.currentTarget.style.backgroundColor = bg;
  };

  const themeMenuItems = (
    <>
      {(
        [
          {
            id: 'apex' as const,
            label: t('theme.apex'),
            icon: <Mountain size={16} strokeWidth={1.5} style={{ color: '#2563EB' }} />,
          },
          {
            id: 'apex-night' as const,
            label: t('theme.apexNight'),
            icon: <Mountain size={16} strokeWidth={1.5} style={{ color: '#60A5FA' }} />,
          },
          {
            id: 'horizon' as const,
            label: t('theme.horizon'),
            icon: <Sun size={16} strokeWidth={1.5} style={{ color: '#2A6B62' }} />,
          },
          {
            id: 'horizon-night' as const,
            label: t('theme.horizonNight'),
            icon: <Moon size={16} strokeWidth={1.5} style={{ color: '#7EB8AE' }} />,
          },
          {
            id: 'dawn' as const,
            label: t('theme.horizonDawn'),
            icon: <Sun size={16} strokeWidth={1.5} style={{ color: '#B45309' }} />,
          },
          {
            id: 'dusk' as const,
            label: t('theme.horizonDusk'),
            icon: <Moon size={16} strokeWidth={1.5} style={{ color: '#93A8E0' }} />,
          },
          {
            id: 'mist' as const,
            label: t('theme.horizonMist'),
            icon: <Cloud size={16} strokeWidth={1.5} style={{ color: '#4B6E5E' }} />,
          },
        ] as const
      )
        .filter((tm) => (SELECTABLE_THEME_MODES as readonly string[]).includes(tm.id))
        .map((tm) => (
          <Menu.Item
            key={tm.id}
            leftSection={tm.icon}
            onClick={() => toggleTheme(tm.id as ThemeMode)}
            style={{
              color: themeColors.text,
              backgroundColor: mode === tm.id ? `${themeColors.accent}22` : 'transparent',
              borderRadius: 8,
              fontWeight: mode === tm.id ? 600 : 500,
              padding: '8px 10px',
              fontSize: 13,
            }}
          >
            {tm.label}
          </Menu.Item>
        ))}
    </>
  );

  return (
    <div
      className={`flex-shrink-0 space-y-0.5 ${isMinimized ? 'px-1.5 py-2' : 'px-2.5 py-2'}`}
      style={{ borderTop: `1px solid ${themeColors.border}` }}
    >
      {userEmail && (
        <Tooltip label={userEmail} position="right" disabled={!isMinimized} withArrow>
          <div className={footerBtnClass} style={{ color: themeColors.text, cursor: 'default' }}>
            <div
              className={`rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${
                isMinimized ? 'w-7 h-7 text-xs' : 'w-6 h-6 text-[11px]'
              }`}
              style={{ backgroundColor: themeColors.primary }}
            >
              {userEmail.charAt(0).toUpperCase()}
            </div>
            {/*
              Just the email. The "Kelola akun" label and its UserCog sat on
              a plain div with no onClick and no route behind it -- there is
              no account page in pageUrl -- so they promised an action that
              did not exist.
            */}
            {!isMinimized && (
              <span className="truncate flex-1 text-left text-[13px]" title={userEmail}>
                {userEmail}
              </span>
            )}
          </div>
        </Tooltip>
      )}

      {canImpersonate && (
        <Menu position="right-start" offset={8} transitionProps={{ transition: 'fade', duration: 150 }}>
          <Menu.Target>
            <Tooltip label={t('sidebar.impersonate')} position="right" disabled={!isMinimized} withArrow>
              <button
                type="button"
                className={footerBtnClass}
                style={{
                  color: isImpersonating ? themeColors.warning : themeColors.text,
                  backgroundColor: isImpersonating ? `${themeColors.warning}15` : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isImpersonating) applyHover(e);
                }}
                onMouseLeave={(e) => {
                  if (!isImpersonating) clearHover(e);
                }}
              >
                <span className="text-[14px] shrink-0">🎭</span>
                {!isMinimized && (
                  <span className="text-[13px] truncate flex-1 text-left">
                    {isImpersonating ? t('sidebar.impersonating') : t('sidebar.impersonate')}
                  </span>
                )}
              </button>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown
            style={{
              backgroundColor: themeColors.card,
              border: `1px solid ${themeColors.border}`,
              borderRadius: 8,
              padding: 12,
              minWidth: 320,
              maxWidth: 400,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <ImpersonationSelect />
          </Menu.Dropdown>
        </Menu>
      )}

      <Menu position={isMinimized ? 'right' : 'top-end'} offset={8}>
        <Menu.Target>
          <Tooltip label={t('sidebar.changeTheme')} position="right" disabled={!isMinimized} withArrow>
            <button
              type="button"
              onClickCapture={hapticMedium}
              className={footerBtnClass}
              style={{ color: themeColors.text, backgroundColor: 'transparent' }}
              onMouseEnter={applyHover}
              onMouseLeave={(e) => clearHover(e)}
            >
              <span className="text-[14px] shrink-0" style={{ color: mutedText }}>
                {ThemeIcon}
              </span>
              {!isMinimized && <span className="text-[13px] truncate flex-1 text-left">{themeLabel}</span>}
            </button>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown
          style={{
            backgroundColor: themeColors.card,
            border: `1px solid ${themeColors.border}`,
            borderRadius: 8,
            padding: 6,
          }}
        >
          {themeMenuItems}
        </Menu.Dropdown>
      </Menu>

      {/* Personal settings. Out of `menus` for the same reason as the changelog
          below, and out of MODULES for one more: this is the page where a user
          turns things off, so narrowing a company's modules must never take it
          away. */}
      <Tooltip label={t('menu.pengaturan')} position="right" disabled={!isMinimized} withArrow>
        <button
          type="button"
          onClick={() => {
            hapticMedium();
            onNavigate?.();
            navigate('/pengaturan');
          }}
          className={footerBtnClass}
          style={{ color: themeColors.text, backgroundColor: highlight === 'pengaturan' ? hoverBg : 'transparent' }}
          onMouseEnter={applyHover}
          onMouseLeave={(e) => clearHover(e, highlight === 'pengaturan' ? hoverBg : 'transparent')}
          aria-label={t('menu.pengaturan')}
          data-nav-footer="pengaturan"
        >
          <Settings size={15} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
          {!isMinimized && <span className="text-[13px] truncate flex-1 text-left">{t('menu.pengaturan')}</span>}
        </button>
      </Tooltip>

      {/* Published changelog. Deliberately NOT a `menus` entry: ALL_MENU_LINKS is
          derived from that array and MenuAccessGuard blocks any path it owns for
          roles without the grant — release notes must reach every user. */}
      <Tooltip label={t('menu.riwayatPembaruan')} position="right" disabled={!isMinimized} withArrow>
        <button
          type="button"
          onClick={() => {
            hapticMedium();
            onNavigate?.();
            navigate(pageUrl.riwayatPembaruan);
          }}
          className={footerBtnClass}
          style={{ color: themeColors.text, backgroundColor: 'transparent' }}
          onMouseEnter={applyHover}
          onMouseLeave={(e) => clearHover(e)}
          aria-label={t('menu.riwayatPembaruan')}
        >
          <History size={15} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
          {!isMinimized && <span className="text-[13px] truncate flex-1 text-left">{t('menu.riwayatPembaruan')}</span>}
          {/* Unread updates (Issue 13). Survives "remind me later": the
              modal stops interrupting after three deferrals, but the
              release notes are still unread and the badge says so. */}
          {unreadPatchLogs > 0 && (
            <span
              aria-label={`${unreadPatchLogs} pembaruan belum dibaca`}
              className="shrink-0 rounded-full px-1.5 text-[10px] font-semibold leading-[16px] text-white"
              style={{ backgroundColor: '#e03131' }}
            >
              {unreadPatchLogs > 9 ? '9+' : unreadPatchLogs}
            </span>
          )}
        </button>
      </Tooltip>

      <TourHelpButton isMinimized={isMinimized} className={footerBtnClass} withAnchor={tourAnchors} />

      <div {...(tourAnchors ? { 'data-tour': 'nav-language' } : {})}>
        <LanguageSwitcher isMinimized={isMinimized} position={isMinimized ? 'right' : 'top-end'} />
      </div>

      <Tooltip label={t('sidebar.logout')} position="right" disabled={!isMinimized} withArrow>
        <button
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          className={`${footerBtnClass} ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ color: themeColors.error, backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            if (!isLoggingOut) e.currentTarget.style.backgroundColor = `${themeColors.error}10`;
          }}
          onMouseLeave={(e) => clearHover(e)}
        >
          <Power size={15} strokeWidth={1.5} className="shrink-0" />
          {!isMinimized && (
            <span className="text-[13px] truncate flex-1 text-left">
              {isLoggingOut ? t('sidebar.loggingOut') : t('sidebar.logout')}
            </span>
          )}
        </button>
      </Tooltip>

      {/* Collapse / expand — desktop only */}
      <Tooltip
        label={
          isMinimized
            ? `${t('sidebar.expand')} ${isMac ? '⌘B' : 'Ctrl+B'}`
            : `${t('sidebar.collapse')} ${isMac ? '⌘B' : 'Ctrl+B'}`
        }
        position="right"
        withArrow
      >
        <button
          type="button"
          onClick={() => {
            hapticFirm();
            onToggleMinimize?.();
          }}
          className={`hidden min-[992px]:flex ${footerBtnClass}`}
          style={{ color: mutedText, backgroundColor: 'transparent' }}
          onMouseEnter={applyHover}
          onMouseLeave={(e) => clearHover(e)}
          aria-label={isMinimized ? t('sidebar.expand') : t('sidebar.collapse')}
        >
          {isMinimized ? (
            <ChevronsRight size={18} strokeWidth={1.5} />
          ) : (
            <>
              <ChevronsLeft size={16} strokeWidth={1.5} className="shrink-0" />
              <span className="text-[13px] truncate flex-1 text-left">{t('sidebar.collapse')}</span>
            </>
          )}
        </button>
      </Tooltip>
    </div>
  );
};

const SidebarV2 = ({ isOpen, isMinimized, toggleDrawer, toggleMinimize, isVisible = true }: SidebarV2Props) => {
  const { logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { userEmail, canImpersonate, isImpersonating, professionalModeEnabled } = useSidebarIdentity();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const hapticLight = useHaptic('light');
  const hapticMedium = useHaptic('medium');
  const hapticFirm = useHaptic('firm');

  const isGatewayUser = userEmail.toLowerCase().includes('gateway');
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const { isDarkSidebar, hoverBg, mutedText, sidebarStyle, accent, aurora } = useSidebarSurface();

  const closeMobileDrawer = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT && isOpen) {
      toggleDrawer();
    }
  };

  // Prevent body scroll when sidebar is open on mobile and portrait tablet view
  useEffect(() => {
    if (isOpen && window.innerWidth < MOBILE_BREAKPOINT) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled, searchQuery });
  const [lockedModuleLabel, setLockedModuleLabel] = useState<string | null>(null);

  // ⌘K / Ctrl+K focuses quick search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (isMinimized) return;
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMinimized]);

  const handleLogout = () => {
    hapticFirm();
    logout(() => {
      notifications.show({ title: 'Success', message: 'Logout success!', color: 'green' });
      navigate('/auth');
    });
  };

  /** Tracks scroll depth to drive the glowing progress indicator. */
  const lastScrollBuzzRef = useRef(0);
  const handleNavScroll = () => {
    const el = navScrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const progress = max > 0 ? Math.min(1, el.scrollTop / max) : 0;
    setScrollProgress(progress);
    // On touch devices fire a throttled light buzz while scrolling (desktop
    // has no motor — the progress glow above is its visual counterpart).
    const now = Date.now();
    if (now - lastScrollBuzzRef.current > 140) {
      lastScrollBuzzRef.current = now;
      haptic(HAPTIC.light);
    }
  };

  const footerBtnClass = isMinimized
    ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150 relative'
    : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150 relative';

  const applyHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
  };
  const clearHover = (e: React.MouseEvent<HTMLElement>, bg = 'transparent') => {
    e.currentTarget.style.backgroundColor = bg;
  };

  return (
    <>
      {/* Mobile overlay drawer backdrop — only below AppShell md */}
      {isVisible && isOpen && (
        <div
          role="button"
          tabIndex={0}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 max-[991px]:block hidden"
          onClick={toggleDrawer}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleDrawer();
          }}
          aria-label="Close navigation overlay"
        />
      )}

      <div
        className={`h-full w-full flex flex-col z-50 jv-glass-surface
          max-[991px]:fixed max-[991px]:inset-y-0 max-[991px]:left-0 max-[991px]:w-[min(17.5rem,85vw)]
          min-[992px]:relative
          ${isOpen ? 'translate-x-0' : 'max-[991px]:-translate-x-full min-[992px]:translate-x-0'}
          transition-transform duration-300 ease-in-out
        `}
        style={sidebarStyle}
      >
        {/* Ambient aurora background — drifting neon blobs behind content */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div
            className="jv-aurora-blob"
            style={{
              top: '-20%',
              left: '-25%',
              width: 320,
              height: 320,
              backgroundColor: aurora[0],
              opacity: 0.4,
            }}
          />
          <div
            className="jv-aurora-blob jv-aurora-blob--2"
            style={{
              bottom: '-15%',
              right: '-30%',
              width: 300,
              height: 300,
              backgroundColor: aurora[1],
              opacity: 0.32,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(120% 60% at 20% 0%, transparent 40%, rgba(255,255,255,0.18) 100%)',
              pointerEvents: 'none',
            }}
          />
        </div>

        <SidebarBrandHeader
          isMinimized={isMinimized}
          isGatewayUser={isGatewayUser}
          onClose={() => {
            hapticLight();
            toggleDrawer();
          }}
        />

        {/* Search + nav */}
        <div
          ref={navScrollRef}
          onScroll={handleNavScroll}
          className={`relative flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent ${
            isMinimized ? 'px-1.5 py-2' : 'px-2.5 py-2'
          }`}
        >
          {/* Scroll progress glow — visible feedback on desktop (no motor)… */}
          {scrollProgress > 0 && scrollProgress < 1 && (
            <div
              className="pointer-events-none absolute right-0 top-0 z-10 h-1 rounded-full jv-active-glow"
              style={{
                width: 3,
                backgroundColor: accent,
                boxShadow: `0 0 10px ${accent}`,
                height: `${Math.max(24, 220 * scrollProgress)}px`,
                transition: 'height 120ms ease-out',
              }}
              aria-hidden="true"
            />
          )}
          {!isMinimized ? (
            <div
              data-tour="nav-search"
              className="flex items-center gap-2 px-2.5 py-1.5 mb-2 rounded-lg border"
              style={{
                borderColor: themeColors.border,
                backgroundColor: isDarkSidebar ? 'rgba(255,255,255,0.04)' : themeColors.card,
              }}
            >
              <Search size={14} strokeWidth={1.5} className="shrink-0" style={{ color: mutedText }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('sidebar.quickSearch')}
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-[13px]"
                style={{ color: themeColors.text }}
              />
              <kbd className="shrink-0 text-[10px] font-medium px-1" style={{ color: mutedText }}>
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </div>
          ) : (
            <Tooltip label="Quick search (expand sidebar)" position="right" withArrow>
              <button
                type="button"
                onClick={() => {
                  hapticMedium();
                  toggleMinimize();
                }}
                className={footerBtnClass}
                style={{ color: mutedText, backgroundColor: 'transparent' }}
                onMouseEnter={applyHover}
                onMouseLeave={(e) => clearHover(e)}
                aria-label="Expand to search"
                data-tour="nav-search"
              >
                <Search size={16} strokeWidth={1.5} />
              </button>
            </Tooltip>
          )}

          <div className="space-y-0.5" data-tour="nav-list">
            {filteredMenus.map((item) => {
              const moduleCode = MODULE_BY_SIDEBAR_LABEL[item.label];
              const locked = moduleCode ? !isFeatureEnabled(moduleCode) : false;
              return (
                // Wrapper carries the onboarding anchor: LinksGroup does not
                // spread unknown props, so data-tour cannot go on it directly.
                <div key={item.label} data-tour={sidebarAnchor(item.label)}>
                  <LinksGroup
                    {...item}
                    isMinimized={isMinimized}
                    onNavigate={closeMobileDrawer}
                    locked={locked}
                    onLockedClick={() => setLockedModuleLabel(item.label)}
                  />
                </div>
              );
            })}
            <FeatureLockedModal
              opened={lockedModuleLabel !== null}
              onClose={() => setLockedModuleLabel(null)}
              moduleLabel={lockedModuleLabel ?? undefined}
            />
            {filteredMenus.length === 0 && !isMinimized && (
              <p className="px-3 py-4 text-[12px] text-center" style={{ color: mutedText }}>
                {t('sidebar.noMatches')}
              </p>
            )}
          </div>
        </div>

        <SidebarFooter
          isMinimized={isMinimized}
          userEmail={userEmail}
          canImpersonate={canImpersonate}
          isImpersonating={isImpersonating}
          onLogout={handleLogout}
          isLoggingOut={isLoading}
          onToggleMinimize={toggleMinimize}
          onNavigate={closeMobileDrawer}
        />
      </div>
    </>
  );
};

export default SidebarV2;
